import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Textarea } from "flowbite-react";
import moment from "moment";
import {
  HiPencil,
  HiOutlineTrash,
  HiCheck,
  HiX,
  HiOutlineDocumentText,
} from "react-icons/hi";
import { CustomButton } from "../atoms/CustomButton";
import { EmptyState } from "../atoms/EmptyState";
import { CharacterCounter } from "../atoms/CharacterCounter";
import NoTranslate from "../atoms/NoTranslate";
import UserAvatar from "../atoms/UserAvatar";

/** One stored note. `author` is optional — Prospect notes have none. */
export interface PanelNote {
  text: string;
  at?: string;
  edited_at?: string;
  author?: { id?: string; name?: string } | null;
}

/** Minimal author-display data the panel needs to render an avatar. */
export interface NoteAuthorInfo {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
}

interface Props {
  notes: PanelNote[];
  /** Add a note. `done()` clears the composer. */
  onAdd: (text: string, done: () => void) => void;
  /** Edit the note at its ORIGINAL index (as stored, not reversed). */
  onEdit: (index: number, text: string, done: () => void) => void;
  /** Delete the note at its ORIGINAL index. */
  onDelete: (index: number) => void;
  maxLength: number;
  minLength?: number;
  isSaving?: boolean;
  disabled?: boolean;
  canDelete?: boolean;
  /**
   * Resolve an author id → display info (avatar + name). When omitted, notes
   * render without an author line (the Prospect case).
   */
  resolveAuthor?: (author?: PanelNote["author"]) => NoteAuthorInfo | undefined;
  /** Height of the scrolling thread (Tailwind class). */
  threadHeightClass?: string;
}

/**
 * Shared notes panel used by both the task detail and the prospect detail so the
 * two never drift apart. Layout: the composer sits at the TOP and the thread
 * scrolls beneath it — this keeps the input in view when the section opens
 * (opening no longer scrolls to the first note) and matches how a chat compose
 * box stays put. Notes render newest-first; each can be edited in place or
 * deleted. Author + avatar show only when `resolveAuthor` is provided.
 */
const NotesPanel: React.FC<Props> = ({
  notes,
  onAdd,
  onEdit,
  onDelete,
  maxLength,
  minLength = 1,
  isSaving = false,
  disabled = false,
  canDelete = true,
  resolveAuthor,
  threadHeightClass = "max-h-[48vh]",
}) => {
  const { t } = useTranslation();
  const [newNote, setNewNote] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  const canAdd = !disabled && !isSaving && newNote.trim().length >= minLength;

  const addNote = () => {
    if (!canAdd) return;
    onAdd(newNote.trim(), () => setNewNote(""));
  };

  const saveEdit = () => {
    if (isSaving || editingIndex === null) return;
    const text = editingText.trim();
    if (text.length < minLength) return;
    onEdit(editingIndex, text, () => {
      setEditingIndex(null);
      setEditingText("");
    });
  };

  const authorName = (author?: PanelNote["author"]): string => {
    if (!author) return "";
    const resolved = resolveAuthor?.(author);
    if (resolved?.name) return resolved.name;
    if (author.name && author.name !== author.id) return author.name;
    return "";
  };

  return (
    <div className="flex flex-col gap-3">
      {!disabled && (
        <div className="shrink-0">
          <Textarea
            value={newNote}
            rows={3}
            maxLength={maxLength}
            placeholder={t("common.notes.placeholder") as string}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote();
            }}
            className="w-full resize-y"
          />
          <CharacterCounter
            currentLength={newNote.length}
            maxLength={maxLength}
            className="mt-1"
          />
          <div className="flex justify-end mt-2">
            <CustomButton
              variant="primary"
              size="sm"
              icon="plus"
              disabled={!canAdd}
              title={t("common.notes.add") as string}
              onClick={addNote}
            />
          </div>
        </div>
      )}

      <div
        className={`${threadHeightClass} overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-2`}
      >
        {notes.length === 0 ? (
          <EmptyState
            icon={<HiOutlineDocumentText size={24} />}
            title={t("common.notes.empty")}
          />
        ) : (
          <ul className="space-y-2">
            {notes
              .map((n, index) => ({ n, index }))
              .reverse()
              .map(({ n, index }) =>
                editingIndex === index ? (
                  <li
                    key={index}
                    className="text-sm bg-white rounded p-2 border border-blue-200"
                  >
                    <Textarea
                      value={editingText}
                      autoFocus
                      rows={3}
                      maxLength={maxLength}
                      onChange={(e) => setEditingText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                          saveEdit();
                        if (e.key === "Escape") setEditingIndex(null);
                      }}
                      className="w-full resize-y"
                    />
                    <div className="flex justify-end gap-1 mt-1">
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        className="rounded-md text-green-600 enabled:hover:text-green-800"
                        onClick={saveEdit}
                        icon={<HiCheck size={16} />}
                        aria-label={t("common.notes.save") as string}
                      />
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        className="rounded-md text-gray-400 enabled:hover:text-gray-700"
                        onClick={() => setEditingIndex(null)}
                        icon={<HiX size={16} />}
                        aria-label={t("common.notes.cancelEdit") as string}
                      />
                    </div>
                  </li>
                ) : (
                  <li
                    key={index}
                    className="text-sm bg-white rounded-lg p-3 flex justify-between gap-2 group border border-gray-100"
                  >
                    <div className="min-w-0 flex gap-2.5">
                      {resolveAuthor &&
                        renderAvatar(
                          resolveAuthor(n.author),
                          authorName(n.author)
                        )}
                      <div className="min-w-0">
                        <span className="whitespace-pre-wrap break-words">
                          <NoTranslate>{n.text}</NoTranslate>
                        </span>
                        <span className="block text-xs text-gray-400 mt-0.5">
                          {authorName(n.author) && (
                            <>
                              <NoTranslate>{authorName(n.author)}</NoTranslate>
                              {" · "}
                            </>
                          )}
                          {n.at ? moment(n.at).format("lll") : ""}
                          {n.edited_at && <> · {t("common.notes.edited")}</>}
                        </span>
                      </div>
                    </div>
                    {!disabled && (
                      <div className="flex items-start gap-1 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <CustomButton
                          type="button"
                          variant="none"
                          size="icon"
                          disabled={isSaving}
                          className="rounded-md text-gray-400 enabled:hover:text-blue-600"
                          onClick={() => {
                            setEditingIndex(index);
                            setEditingText(n.text);
                          }}
                          icon={<HiPencil size={18} />}
                          aria-label={t("common.notes.edit") as string}
                        />
                        {canDelete && (
                          <CustomButton
                            type="button"
                            variant="none"
                            size="icon"
                            disabled={isSaving}
                            className="rounded-md text-gray-400 enabled:hover:text-red-600"
                            onClick={() => onDelete(index)}
                            icon={<HiOutlineTrash size={18} />}
                            aria-label={t("common.notes.delete") as string}
                          />
                        )}
                      </div>
                    )}
                  </li>
                )
              )}
          </ul>
        )}
      </div>
    </div>
  );
};

/** Author avatar, or a lettered fallback when the author can't be resolved. */
function renderAvatar(info: NoteAuthorInfo | undefined, name: string) {
  if (info && (info.firstName || info.lastName || info.email || info.avatar)) {
    return (
      <UserAvatar
        firstName={info.firstName}
        lastName={info.lastName}
        email={info.email}
        avatar={info.avatar}
        sizeClass="w-7 h-7"
        textClass="text-xs"
      />
    );
  }
  return (
    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-semibold shrink-0">
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

export default NotesPanel;
